import { useQuery } from '@tanstack/react-query';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/series-label';
import HighchartsReact from 'highcharts-react-official';
import { merge } from 'lodash';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLE } from '@/common/role';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useTheme } from '@/context/ThemeContext';
import { usePermission } from '@/hooks/use-permission';
import { useScreen } from '@/hooks/use-screen';
import { HIGH_CHART_DARK_THEME, HIGH_CHART_LIGHT_THEME } from '@/lib/highchart/setup';
import { cn, formatDateTime } from '@/lib/utils';
import { useQueryGetTPSPerfStats } from '@/queries/assets/sensors/tps/get-per-stats-chart';
import { AbsoluteLoader } from '../../absoluteLoader';
import { AccessDeniedView } from '../AccessDeniedView';
import { EmptyChart } from '../EmptyChart';

type BaseChartOptionsParams = {
	showLegend?: boolean;
	avgValue?: number;
	screen?: Screen;
};

function averageNumeric(arr: unknown[]): number {
	const vals = arr.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
	return vals.length ? vals.reduce((sum, v) => sum + v, 0) / vals.length : 0.0;
}

/** Flags the latest value when it is more than 50% away from the period average. */
function isAlert(currentValue: number | null, avgValue: number): boolean {
	if (currentValue === null || avgValue <= 0) return false;
	const deviationPercent = (Math.abs(currentValue - avgValue) / avgValue) * 100;
	return deviationPercent > 50;
}

function baseChartOptions(
	yAxisName: string,
	{ showLegend, avgValue, screen }: BaseChartOptionsParams = {},
): Highcharts.Options {
	const hasAvg = Number.isFinite(avgValue) && (avgValue || 0) > 0;
	const plotLines: Highcharts.YAxisPlotLinesOptions[] = hasAvg
		? [
				{
					color: 'red',
					width: 2,
					value: avgValue,
					dashStyle: 'ShortDash',
					zIndex: 5,
					label: { text: `Ø ${(avgValue || 0).toFixed(2)}`, style: { color: 'red' } },
				},
			]
		: [];
	return {
		chart: {
			zooming: {
				type: 'x',
			},
			height: Math.max((screen?.height || 0) / 6, 190),
		},
		legend: { enabled: showLegend },
		title: { text: undefined },
		xAxis: { type: 'datetime', visible: false },
		yAxis: [{ title: { text: yAxisName }, plotLines }],
		tooltip: { shared: true },
	};
}

export type PerformanceDashProps = {
	assetId: string;
};

export function PerformanceDash({ assetId }: PerformanceDashProps) {
	const { t } = useTranslation();
	const { isDarkTheme } = useTheme();
	const [days, setDays] = useState(31);
	const haveReadPermission = usePermission(ROLE.SENSORS_TPS_PERFSTATS_READ);
	const { data: chartResponse, isLoading } = useQuery(useQueryGetTPSPerfStats(assetId, { days }));
	const screen = useScreen();
	const chartData = chartResponse?.chartData || [];
	const lastRecord = chartResponse?.lastPerfStat;
	const daysOptions = [
		{ value: 7, label: t('health.charts.last7Days'), id: useId() },
		{ value: 31, label: t('health.charts.last31Days'), id: useId() },
	];
	const chartFinalData = useMemo(() => {
		const numObjData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.numObj] as [number, number],
		);
		const numTrafoData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.numTrafo] as [number, number],
		);
		const timeTpsData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.timeTps] as [number, number],
		);
		const timeProcData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.timeProc] as [number, number],
		);
		const timeTransData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.timeTrans] as [number, number],
		);
		const timeCsvData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.timeCsv] as [number, number],
		);
		const numOutData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.numOut] as [number, number],
		);
		const numWLData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.numWL] as [number, number],
		);
		const numWHData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.numWH] as [number, number],
		);
		const numALData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.numAL] as [number, number],
		);
		const avgNumObj = averageNumeric(chartData.map((r) => r.numObj));
		const avgNumTrafo = averageNumeric(chartData.map((r) => r.numTrafo));
		const avgTimeTps = averageNumeric(chartData.map((r) => r.timeTps));
		const avgTimeProc = averageNumeric(chartData.map((r) => r.timeProc));
		const avgTimeTrans = averageNumeric(chartData.map((r) => r.timeTrans));
		const avgTimeCsv = averageNumeric(chartData.map((r) => r.timeCsv));
		const avgNumOut = averageNumeric(chartData.map((r) => r.numOut));
		const objectPointsLabel = t('health.charts.objectPoints');
		const fixedPointsLabel = t('health.charts.fixedPoints');
		const measurementRoundLabel = t('health.charts.measurementRound');
		const processingLabel = t('health.charts.processing');
		const transformationLabel = t('health.charts.transformation');
		const csvExportLabel = t('health.charts.csvExport');
		const outliersLabel = t('health.charts.outliers');
		const warnAlarmMessagesTotalLabel = t('health.charts.warnAlarmMessagesTotal');
		return [
			{
				key: 'objectPoints',
				label: objectPointsLabel,
				baseOptions: baseChartOptions('', { avgValue: avgNumObj, screen }),
				series: [
					{ name: objectPointsLabel, data: numObjData, type: 'line', zIndex: 5 },
				] as Highcharts.SeriesOptionsType[],
				kpi: lastRecord?.numObj,
				isAlert: isAlert(lastRecord?.numObj ?? null, avgNumObj),
			},
			{
				key: 'fixedPoints',
				label: fixedPointsLabel,
				baseOptions: baseChartOptions('', { avgValue: avgNumTrafo, screen }),
				series: [{ name: fixedPointsLabel, data: numTrafoData, type: 'line', zIndex: 5 }],
				kpi: lastRecord?.numTrafo,
				isAlert: isAlert(lastRecord?.numTrafo ?? null, avgNumTrafo),
			},
			{
				key: 'measurementRound',
				label: measurementRoundLabel,
				baseOptions: baseChartOptions('', { avgValue: avgTimeTps, screen }),
				series: [{ name: measurementRoundLabel, data: timeTpsData, type: 'line', zIndex: 5 }],
				kpi: lastRecord?.timeTps,
				kpiSuffix: 's',
				isAlert: isAlert(lastRecord?.timeTps ?? null, avgTimeTps),
			},
			{
				key: 'processing',
				label: processingLabel,
				baseOptions: baseChartOptions('', { avgValue: avgTimeProc, screen }),
				series: [{ name: processingLabel, data: timeProcData, type: 'line', zIndex: 5 }],
				kpi: lastRecord?.timeProc,
				kpiSuffix: 's',
				isAlert: isAlert(lastRecord?.timeProc ?? null, avgTimeProc),
			},
			{
				key: 'transformation',
				label: transformationLabel,
				baseOptions: baseChartOptions('', { avgValue: avgTimeTrans, screen }),
				series: [{ name: transformationLabel, data: timeTransData, type: 'line', zIndex: 5 }],
				kpi: lastRecord?.timeTrans,
				kpiSuffix: 's',
				isAlert: isAlert(lastRecord?.timeTrans ?? null, avgTimeTrans),
			},
			{
				key: 'csvExport',
				label: csvExportLabel,
				baseOptions: baseChartOptions('', { avgValue: avgTimeCsv, screen }),
				series: [{ name: csvExportLabel, data: timeCsvData, type: 'line', zIndex: 5 }],
				kpi: lastRecord?.timeCsv,
				kpiSuffix: 's',
				isAlert: isAlert(lastRecord?.timeCsv ?? null, avgTimeCsv),
			},
			{
				key: 'outliers',
				label: outliersLabel,
				baseOptions: baseChartOptions('', { avgValue: avgNumOut, screen }),
				series: [{ name: outliersLabel, data: numOutData, type: 'line', zIndex: 5 }],
				kpi: lastRecord?.numOut,
				isAlert: isAlert(lastRecord?.numOut ?? null, avgNumOut),
			},
			{
				key: 'warnAlarmMessagesTotal',
				label: warnAlarmMessagesTotalLabel,
				baseOptions: baseChartOptions('', { showLegend: true, screen }),
				series: [
					{ name: 'WL', data: numWLData, color: '#198754', zIndex: 3 }, // grün
					{ name: 'WH', data: numWHData, color: '#FFC107', zIndex: 2 }, // gelb
					{ name: 'AL', data: numALData, color: '#DC3545', zIndex: 4 }, // rot
				],
				kpi: lastRecord ? lastRecord.numWL + lastRecord.numWH + lastRecord.numAL : undefined,
			},
		];
	}, [chartData, lastRecord, screen, t]);
	const charts = useMemo(
		() =>
			chartFinalData.map((data) => ({
				...data,
				baseOptions: merge(
					structuredClone(isDarkTheme ? HIGH_CHART_DARK_THEME : HIGH_CHART_LIGHT_THEME),
					data.baseOptions,
				),
			})),
		[chartFinalData, isDarkTheme],
	);
	if (!haveReadPermission) {
		return <AccessDeniedView />;
	}
	if (isLoading) {
		return (
			<div className="h-50 relative w-full">
				<AbsoluteLoader className="bg-transparent" isLoading />
			</div>
		);
	}
	if (!chartData?.length) {
		return <EmptyChart title={t('health.charts.noDataTitle')} subtitle={t('health.charts.noDataSubtitle')} />;
	}
	return (
		<div className="flex-1 overflow-auto p-6 m-0">
			<div className="space-y-3 @container">
				<div className="grid grid-cols-[1fr_1fr_1fr] gap-2 text-xs @3xl:text-sm @3xl:grid-cols-[1fr_1fr_1fr]">
					<Card className="gap-1 p-2">
						<CardContent className="p-1 justify-center flex flex-row gap-1 font-semibold">
							<span>{t('health.charts.currentEpoch')} </span>
							<span>{lastRecord?.measEpoch ? lastRecord.measEpoch : '-'}</span>
						</CardContent>
					</Card>
					<Card className="gap-1 p-2">
						<CardContent className="p-1 justify-center flex flex-row gap-1 font-semibold">
							<span>{t('health.charts.currentEpochStart')} </span>
							<span>{lastRecord?.measGroup ? formatDateTime(new Date(lastRecord.measGroup).getTime()) : '-'}</span>
						</CardContent>
					</Card>
					<Card className="gap-1 p-2">
						<CardContent className="p-1 flex flex-row gap-1 font-semibold">
							<RadioGroup
								onValueChange={(value) => setDays(Number(value))}
								defaultValue={String(days)}
								className="grid grid-cols-2"
							>
								{daysOptions.map((option) => (
									<div key={option.id} className="flex gap-1">
										<RadioGroupItem value={String(option.value)} id={option.id} className="cursor-pointer" />
										<Label htmlFor={option.id} className="cursor-pointer">
											{option.label}
										</Label>
									</div>
								))}
							</RadioGroup>
						</CardContent>
					</Card>
				</div>
				<div className="grid grid-cols-1 @xl:grid-cols-2 @3xl:grid-cols-3 gap-3">
					{charts.map((chart) => (
						<Card key={chart.key} className="border py-0 gap-1">
							<CardTitle className="p-2 space-x-2">
								<span>{chart.label}</span>
								<span className="text-xl">
									({chart.kpi || 0}
									{chart.kpiSuffix ? ` ${chart.kpiSuffix}` : ''})
								</span>
							</CardTitle>
							<CardContent className={cn('p-1', chart.isAlert ? 'bg-red-400' : '')}>
								<HighchartsReact
									highcharts={Highcharts}
									options={{
										...chart.baseOptions,
										series: chart.series,
									}}
								/>
							</CardContent>
						</Card>
					))}
				</div>
			</div>
		</div>
	);
}
